import { useCallback, useEffect } from 'react';
import ContentViewport from '../../components/ContentViewport';
import LessonCompleteInterstitial from '../../components/ContentInterstitials/LessonCompleteInterstitial';
import UsageTracker from '../../utils/UsageTracker';
import { SCREENS, SUB_SCREENS } from '../../constants/Tracking';
import styled, { mediaQuery, css, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { RecommendationsGrid, useRecommendationsForLesson } from '../../features/recommendations';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Section } from 'foundations-components/transitional/components/Section';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Subheader } from 'foundations-components/transitional/components/Header';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { LearningContentCard } from '../../features/cards';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MAX_RECOMMENDATIONS_FOR_LESSON_SIZE = 3;
const MobilePadded = styled.div.withConfig({
  displayName: "RecommendedLessonsContainer__MobilePadded",
  componentId: "xm4m67-0"
})(["margin-top:24px;padding-left:12px;padding-right:12px;", ""], mediaQuery('notMobile', css(["padding-left:30px;padding-right:30px;"])));
const useRecommendedLessons = (lessonId, language) => {
  const {
    recommendationsForLesson,
    recommendationsForLessonError,
    recommendationsForLessonLoading
  } = useRecommendationsForLesson(lessonId);
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: (recommendationsForLesson === null || recommendationsForLesson === void 0 ? void 0 : recommendationsForLesson.recommendations.slice(0, MAX_RECOMMENDATIONS_FOR_LESSON_SIZE).map(({
      recommendationId,
      recommendationType
    }) => {
      return {
        id: recommendationId,
        type: recommendationType,
        language
      };
    })) || [],
    skip: !(recommendationsForLesson !== null && recommendationsForLesson !== void 0 && recommendationsForLesson.recommendations.length)
  });
  return {
    recommendations: cardModels,
    isRequesting: cardModelsLoading || recommendationsForLessonLoading,
    hasError: cardModelsError || recommendationsForLessonError
  };
};
const RecommendedLessonsContainer = ({
  lesson: {
    id: lessonId,
    name,
    language
  }
}) => {
  const {
    isTablet
  } = useMediaQuery();
  const {
    recommendations,
    isRequesting,
    hasError
  } = useRecommendedLessons(lessonId, language);
  useEffect(() => {
    UsageTracker.track('subpageView', {
      screen: SCREENS.LESSON,
      subscreen: SUB_SCREENS.RECOMMENDATIONS,
      lessonId
    });
  }, [lessonId]);
  const onClickRecommendedLesson = useCallback(({
    id
  }) => {
    UsageTracker.track('interaction', {
      action: 'click-recommendation',
      screen: SCREENS.LESSON,
      subscreen: SUB_SCREENS.RECOMMENDATIONS,
      recommendationSeedId: lessonId,
      recommendationType: 'LESSON',
      lessonId: id
    });
  }, [lessonId]);
  if (isRequesting) {
    return /*#__PURE__*/_jsx(ContentViewport, {
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 370
      })
    });
  }
  if (hasError || !(recommendations !== null && recommendations !== void 0 && recommendations.length)) {
    return /*#__PURE__*/_jsx(ContentViewport, {
      children: /*#__PURE__*/_jsx(LessonCompleteInterstitial, {})
    });
  }
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(MobilePadded, {
      "data-test-id": "track-recommendations-page",
      children: /*#__PURE__*/_jsx(ContentViewport, {
        children: /*#__PURE__*/_jsx(View, {
          marginBlock: "600",
          paddingInline: isTablet ? '300' : undefined,
          children: /*#__PURE__*/_jsxs(Section, {
            children: [/*#__PURE__*/_jsx(Subheader, {
              details: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.lessonInterstitials.recommendations.lessonRecommendations.body",
                options: {
                  lessonName: name
                }
              }),
              marginBlock: "600",
              paddingBlock: "0",
              title: /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "learningCenter.lessonInterstitials.recommendations.lessonRecommendations.heading"
              })
            }), /*#__PURE__*/_jsx(RecommendationsGrid, {
              children: recommendations.map(cardModel => /*#__PURE__*/_jsx(LearningContentCard, {
                item: cardModel,
                onItemClick: onClickRecommendedLesson,
                overviewType: "recommendations"
              }, cardModel.id))
            })]
          })
        })
      })
    })
  });
};
export default RecommendedLessonsContainer;